import { Injectable } from '@angular/core';
import { ApolloError } from '@apollo/client';
import {
	catchError,
	combineLatest,
	firstValueFrom,
	forkJoin,
	from,
	map,
	NEVER,
	Observable,
	of,
	switchMap,
	tap
} from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { LiveDeviceDiscoveryApiService } from '@shure/shared/angular/data-access/device-discovery';
import {
	DeviceAuthenticationStateChange,
	ErrorCode,
	NodeChangeType,
	CombiningSubscriptionManager,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import { DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';

import {
	AuthenticationInput,
	AuthenticationResponse,
	AuthenticationResponseSet,
	AuthenticationResultCode,
	DeviceAuthenticationCoreApiService,
	DeviceAuthenticationStatusService
} from '../api';

import {
	AuthenticateMutationGQL,
	BypassInitialCredentialsMutationGQL,
	ClearPasswordMutationGQL,
	CreateCredentialsMutationGQL,
	CreateKeyringMutationGQL,
	DeviceAuthenticationFragment,
	DeviceAuthenticationSubscriptionGQL,
	DeviceAuthenticationSubscriptionOpResult,
	RemoveCredentialsMutationGQL,
	UpdateCredentialsMutationGQL,
	UpdateKeyringMutationGQL,
	VerifyCredentialsMutationGQL
} from './graphql/generated/authentication.generated';
import { SysApiDeviceAuthenticationKeyringService } from './sys-api-device-authentication-keyring.service';

@Injectable({ providedIn: 'root' })
export class SysApiDeviceAuthenticationApiService implements DeviceAuthenticationCoreApiService {
	private readonly logger: ILogger;
	public readonly defaultAccessLevel = 'Admin';
	private deviceQueryIdRecord: Record<string, string> = {};
	private devicesGettingPasswordSet: AuthenticationInput[] = [];

	private readonly devicesAuthenticationGqlSubscriptions: CombiningSubscriptionManager<DeviceAuthenticationSubscriptionOpResult>;

	constructor(
		logger: ILogger,
		private readonly deviceDiscoveryService: LiveDeviceDiscoveryApiService,
		private readonly sysApiDeviceAuthenticationKeyringService: SysApiDeviceAuthenticationKeyringService,
		private readonly authenticateMutationGQL: AuthenticateMutationGQL,
		private readonly bypassInitialCredentialsMutationGQL: BypassInitialCredentialsMutationGQL,
		private readonly createKeyringMutationGQL: CreateKeyringMutationGQL,
		private readonly createCredentialsMutationGQL: CreateCredentialsMutationGQL,
		private readonly removeCredentialsMutationGQL: RemoveCredentialsMutationGQL,
		private readonly updateCredentialsMutationGQL: UpdateCredentialsMutationGQL,
		private readonly updateKeyringMutationGQL: UpdateKeyringMutationGQL,
		private readonly verifyCredentialsMutationGQL: VerifyCredentialsMutationGQL,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		private readonly clearPasswordMutationGQL: ClearPasswordMutationGQL,
		deviceSubscriptionGql: DeviceAuthenticationSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiAuthenticationService');

		this.devicesAuthenticationGqlSubscriptions =
			new CombiningSubscriptionManager<DeviceAuthenticationSubscriptionOpResult>(
				{
					name: 'SysApiDeviceAuthenticationApiService',
					changeTypes: [NodeChangeType.DeviceAuthentication],
					subscriptionGql: deviceSubscriptionGql,
					onUpdate: (targetId, change): void => {
						const nodeChanges = <DeviceAuthenticationFragment>change.data?.nodeChanges;
						const authentication = nodeChanges.features?.authentication;
						const verifyCredentialsRequired = (<DeviceAuthenticationStateChange>authentication)
							?.verifyCredentialsRequired;
						if (verifyCredentialsRequired === true) {
							void this.triggerReauthenticate(targetId);
						}
					}
				},
				logger,
				subscriptionManagerService
			);

		this.deviceDiscoveryService
			.getDiscoveryEvents$()
			.pipe(
				switchMap(async (discovered) => {
					if (discovered.updated) {
						const device = discovered.updated;
						if (device.status === DeviceStatus.Online) {
							const authResponse = await this.validateAuthentication(discovered.updated.id, true);
							this.logger.trace('device discovery', 'reauth validate status', {
								status: AuthenticationResultCode[authResponse],
								deviceId: device.id,
								deviceModel: device.model
							});
						}
					}
					return NEVER;
				})
			)
			.subscribe();
	}

	public async triggerReauthenticate(deviceId: string): Promise<void> {
		if (this.devicesGettingPasswordSet.some((device) => device.deviceId === deviceId)) {
			this.logger.warning('triggerReauthenticate()', 'Abort by pending operation', deviceId);
			return;
		}

		const reauth = await this.validateAuthentication(deviceId, true);
		if (reauth === AuthenticationResultCode.Ok) {
			this.logger.information('triggerReauthenticate()', 'validate ok', deviceId);
		} else {
			this.logger.warning('triggerReauthenticate()', 'Expired authentication', deviceId);
			await this.removeDeviceFromKeyring(deviceId);
		}
	}

	public async validateAuthentication(deviceId: string, verify: boolean): Promise<AuthenticationResultCode> {
		const isAuthenticated = await this.checkDeviceAuthenticationState(deviceId);

		const keyring = this.getSessionKeyring();
		if (!isAuthenticated || keyring === undefined) {
			return AuthenticationResultCode.NotAuthenticated;
		}

		if (!verify) {
			return AuthenticationResultCode.Ok;
		}
		const mutationParams = { authenticateId: deviceId, keyring };

		const authReponse = await firstValueFrom(
			this.authenticateMutationGQL.mutate(mutationParams).pipe(
				map((result) => {
					if (result.errors) {
						this.logger.warning('validateAuthentication()', 'authenticate device bad password', {
							deviceId,
							result
						});
						this.deviceAuthenticationStatusService.resetDeviceState(deviceId);
						return AuthenticationResultCode.BadPassword;
					}

					if (result.data?.authenticate?.device?.id) {
						this.logger.trace('validateAuthentication()', 'authenticate device success', deviceId);
						return AuthenticationResultCode.Ok;
					}

					this.logger.warning('validateAuthentication()', 'missing device response', result);
					return AuthenticationResultCode.Error;
				}),
				catchError((apolloError: ApolloError) => {
					this.logger.error('validateAuthentication()', 'api error', { apolloError });
					this.deviceAuthenticationStatusService.resetDeviceState(deviceId);
					const passwordreject = apolloError.graphQLErrors.some(
						/* eslint-disable-next-line dot-notation -- private accessor */
						(error) => error.extensions && error.extensions['code'] === 'AUTHENTICATE_ERROR'
					);
					return of(passwordreject ? AuthenticationResultCode.BadPassword : AuthenticationResultCode.Error);
				})
			)
		);

		return authReponse;
	}

	public async setupWithoutAuthentication(deviceIds: string[]): Promise<AuthenticationResponseSet> {
		if (deviceIds.length === 0) {
			return { deviceResponses: [], response: AuthenticationResultCode.Ok };
		}

		const deviceAuths = await firstValueFrom(
			combineLatest(deviceIds.map((id) => this.deviceAuthenticationStatusService.getDeviceState$(id)))
		);

		const removeDeviceFromKeyringPromises = deviceAuths.map((authDevice) => {
			this.logger.trace('setupWithoutAuthentication', 'removing old auth from device', authDevice);
			return this.removeDeviceFromKeyring(authDevice.id);
		});
		await Promise.all(removeDeviceFromKeyringPromises);

		const authenticationsRequested = deviceIds.map((id) => ({ id, accessLevel: this.defaultAccessLevel }));

		const setupResult = this.bypassInitialCredentialsMutationGQL
			.mutate({
				bypassInitialCredentialInputs: authenticationsRequested
			})
			.pipe(
				map((result) => {
					if (result.errors) {
						throw result.errors;
					}
					const responsesWithDeviceId = result.data?.bypassInitialCredentials.filter(
						(f) => f && f.device?.id
					);
					const deviceResponses = responsesWithDeviceId
						? this.mapAuthenticateReponseSet(responsesWithDeviceId)
						: { response: AuthenticationResultCode.Ok, deviceResponses: [] };

					return deviceResponses;
				}),
				catchError((error: Error) => {
					this.logger.error('setupPassword()', 'Failed to create password on deviceSet', {
						devices: deviceIds.map((a) => ({
							id: a,
							passphrase: 'NA'
						})),
						error
					});
					return of({ response: AuthenticationResultCode.Error, deviceResponses: undefined });
				})
			);

		return await firstValueFrom(setupResult);
	}

	public setupPassword(authInfo: AuthenticationInput[]): Observable<AuthenticationResponseSet> {
		const credentials = authInfo.map((a) => {
			return { id: a.deviceId, passphrase: a.password, accessLevel: this.defaultAccessLevel };
		});
		return this.createCredentialsMutationGQL
			.mutate({
				createCredentialsInputs: credentials
			})
			.pipe(
				map((result) => {
					if (result.errors) {
						throw result.errors;
					}
					const deviceResponses = result.data?.createCredentials.map((cc) => {
						return this.validateDeviceResponse(cc);
					});

					const setResponse =
						!deviceResponses?.length ||
						deviceResponses.every((e) => e.response !== AuthenticationResultCode.Ok)
							? AuthenticationResultCode.Rejected
							: AuthenticationResultCode.Ok;

					return { response: setResponse, deviceResponses };
				}),
				catchError((error: Error) => {
					this.logger.error('setupPassword()', 'Failed to create password on deviceSet', {
						devices: authInfo.map((a) => ({
							id: a.deviceId,
							passphrase: 'NA'
						})),
						error
					});
					return of({ response: AuthenticationResultCode.Error, deviceResponses: undefined });
				})
			);
	}

	public setupPasswordAndAuthenticate(authInfo: AuthenticationInput[]): Observable<AuthenticationResponseSet> {
		this.devicesGettingPasswordSet = authInfo;
		return this.setupPassword(authInfo).pipe(
			switchMap((response) => {
				if (response.response !== AuthenticationResultCode.Ok) {
					return of(response);
				}

				const confirmedPasswordFeatures = response.deviceResponses?.filter((f) => {
					return f.response === AuthenticationResultCode.Ok;
				});
				const authenticateDevices = authInfo.filter(
					(f) => confirmedPasswordFeatures?.some((match) => match.deviceId === f.deviceId)
				);

				return this.updateKeyring$(authenticateDevices).pipe(
					map((keyringUpdate) => {
						return {
							response: keyringUpdate,
							deviceResponses: response.deviceResponses
						};
					}),
					tap((_) => (this.devicesGettingPasswordSet = []))
				);
			})
		);
	}

	public updatePassword(deviceIds: string[], passphrase: string): Observable<AuthenticationResultCode> {
		this.logger.trace('updatePassword()', 'update password on device', { deviceIds });
		const keyring = this.sysApiDeviceAuthenticationKeyringService.getForMutation();
		if (!keyring) {
			this.logger.error('updatePassword()', 'Lost Keyring! Unable to continue', { deviceIds });
			return of(AuthenticationResultCode.NotAuthenticated);
		}

		const deviceInputs = deviceIds.map((deviceId) => {
			return {
				accessLevel: this.defaultAccessLevel,
				id: deviceId,
				newPassphrase: passphrase
			};
		});
		return this.updateCredentialsMutationGQL
			.mutate({
				updateCredentialsInputs: deviceInputs,
				updateCredentialsKeyring: keyring
			})
			.pipe(
				switchMap((result) => {
					if (result.errors) {
						throw result.errors;
					}

					const resultErrors = result.data?.updateCredentials?.filter((f) => f.error);
					if (resultErrors !== undefined && resultErrors.length > 0) {
						return of(AuthenticationResultCode.Error);
					}

					return this.authenticate(
						deviceIds.map((deviceId) => {
							return {
								deviceId: deviceId,
								password: passphrase
							};
						})
					).pipe(map((authResult) => authResult.response));
				}),
				catchError((error: Error) => {
					this.logger.error('updatePassword()', 'Failed to re-authorize device', { deviceIds, error });
					return of(AuthenticationResultCode.Error);
				})
			);
	}

	public async removePassword(deviceId: string): Promise<AuthenticationResultCode> {
		this.logger.trace('removePassword()', 'Remove password from device', { deviceId });

		const keyring = this.sysApiDeviceAuthenticationKeyringService.getForMutation();
		if (!keyring) {
			this.logger.error('removePassword()', 'Missing authentication', { deviceId });
			return AuthenticationResultCode.NotAuthenticated;
		}

		const authState = await firstValueFrom(this.deviceAuthenticationStatusService.getDeviceState$(deviceId));
		if (!authState.authenticated) {
			this.logger.trace('removePassword()', 'Skipped un-authenticated device', { deviceId });
			return AuthenticationResultCode.Ok;
		}

		const query = this.removeCredentialsMutationGQL
			.mutate({
				removeCredentialsInputs: { accessLevel: this.defaultAccessLevel, id: deviceId },
				removeCredentialsKeyring: keyring
			})
			.pipe(
				map((result) => {
					if (result.errors) {
						throw result.errors;
					}

					const resultErrors = result.data?.removeCredentials?.filter((f) => f.error);
					if (resultErrors !== undefined && resultErrors.length > 0) {
						return AuthenticationResultCode.Rejected;
					}
					const hasConfirmation = result.data?.removeCredentials
						.map((item) => item.device?.id)
						.includes(deviceId);
					if (hasConfirmation) {
						this.removeDeviceFromKeyring(deviceId);
						return AuthenticationResultCode.Ok;
					}
					return AuthenticationResultCode.Rejected;
				}),
				catchError((error: Error) => {
					this.logger.error('removePassword()', 'Failed to unauthorize device', { deviceId, error });
					return of(AuthenticationResultCode.Error);
				})
			);
		return await firstValueFrom(query);
	}

	public sessionKeyringAddDevices(deviceIds: string[], updatedKeyring: string): void {
		deviceIds.forEach((deviceId) => {
			this.logger.warning('sessionKeyringAddDevices', 'DeviceAuthenticationStatusService add device', {
				deviceIds
			});
			this.deviceAuthenticationStatusService.setDeviceState(deviceId, true);

			const queryId = uuidv4();
			this.devicesAuthenticationGqlSubscriptions.subscribe(queryId, deviceId);
			this.deviceQueryIdRecord[deviceId] = queryId;
		});
		this.sysApiDeviceAuthenticationKeyringService.set(updatedKeyring);
	}

	public sessionKeyringRemoveDevices(deviceIds: string[], updatedKeyring: string): void {
		deviceIds.forEach((deviceId) => {
			this.deviceAuthenticationStatusService.resetDeviceState(deviceId);
		});
		this.sysApiDeviceAuthenticationKeyringService.set(updatedKeyring);
	}

	public authenticate(authInfo: AuthenticationInput[]): Observable<AuthenticationResponseSet> {
		return this.verify(authInfo).pipe(
			switchMap((verifyResult) => {
				if (verifyResult.response !== AuthenticationResultCode.Ok) {
					return of(verifyResult);
				}
				const verifiedDevices = verifyResult.deviceResponses?.filter(
					(returnedDevices) => returnedDevices.response === AuthenticationResultCode.Ok
				);

				const queuedDevices = authInfo.filter((a) => verifiedDevices?.some((s) => s.deviceId === a.deviceId));

				return this.updateKeyring$(queuedDevices).pipe(
					tap((response) => {
						if (response === AuthenticationResultCode.Ok) {
							verifiedDevices?.forEach((device) =>
								this.deviceAuthenticationStatusService.setDeviceState(device.deviceId, true)
							);
						}
					}),
					map((keyringUpdate) => ({ response: keyringUpdate, deviceResponses: verifyResult.deviceResponses }))
				);
			}),
			catchError((error: Error) => {
				const deviceList = authInfo.map((info) => {
					return info.deviceId;
				});
				this.logger.error('authenticate()', 'Failed to authenticate device', { deviceList, error });
				return of({ response: AuthenticationResultCode.Rejected, deviceResponses: [] });
			})
		);
	}

	public selectReadOnlyMode(deviceId: string): void {
		this.deviceAuthenticationStatusService.setDeviceState(deviceId, undefined, true);
	}

	public deselectReadOnlyMode(deviceId: string): void {
		this.deviceAuthenticationStatusService.setDeviceState(deviceId, undefined, false);
	}

	/* istanbul ignore next */
	public verify(authInfo: AuthenticationInput[]): Observable<AuthenticationResponseSet> {
		return this.verifyCredentialsMutationGQL
			.mutate({
				verifyCredentialsInput: authInfo.map((auth) => {
					return { id: auth.deviceId, passphrase: auth.password, accessLevel: this.defaultAccessLevel };
				})
			})
			.pipe(
				map((result) => {
					if (result.errors) {
						this.logger.error('verify()', 'response GQL error', result);
						return { response: AuthenticationResultCode.Error, deviceResponses: undefined };
					}

					const resultSet = result.data?.verifyCredentials;
					if (resultSet === undefined) {
						return { response: AuthenticationResultCode.Rejected, deviceResponses: undefined };
					}
					return this.mapAuthenticateReponseSet(resultSet);
				}),
				catchError((error: Error) => /* istanbul ignore next */ {
					const deviceIds = authInfo.map((f) => f.deviceId);
					this.logger.information('verify()', 'Failed to verify device', { deviceIds, error });
					return of({
						response: AuthenticationResultCode.Error,
						deviceResponses: authInfo.map((device) => ({
							response: AuthenticationResultCode.Error,
							deviceId: device.deviceId
						}))
					});
				})
			);
	}

	public getSessionKeyring(): string | undefined {
		return this.sysApiDeviceAuthenticationKeyringService.get();
	}
	public clearPassword(deviceId: string): Observable<AuthenticationResultCode> {
		this.logger.trace('clearPassword()', 'Clear password from device', { deviceId });

		return this.clearPasswordMutationGQL
			.mutate({
				deviceId
			})
			.pipe(
				map((result) => {
					if (result.errors) {
						throw result.errors;
					}

					const resultErrors = result.data?.forceResetCredentials?.error;
					if (resultErrors !== undefined) {
						return AuthenticationResultCode.Rejected;
					}

					const hasConfirmation = result.data?.forceResetCredentials.device?.id === deviceId;
					if (hasConfirmation) {
						this.removeDeviceFromKeyring(deviceId);
						return AuthenticationResultCode.Ok;
					}
					return AuthenticationResultCode.Rejected;
				}),
				catchError((error: Error) => {
					this.logger.error('ClearPassword()', 'Failed to clear password from device', { deviceId, error });
					return of(AuthenticationResultCode.Error);
				})
			);
	}

	public reauthenticate(deviceIds: string[]): Observable<void[]> {
		return forkJoin(deviceIds.map((deviceId) => from(this.triggerReauthenticate(deviceId))));
	}

	/* istanbul ignore next */
	private createKeyring$(authInfo: AuthenticationInput[]): Observable<AuthenticationResultCode> {
		const deviceIds = authInfo.map((a) => a.deviceId);
		this.logger.trace('createKeyring()', 'start', deviceIds);
		const keyringInput = authInfo.map((a) => ({ id: a.deviceId, passphrase: a.password }));
		return this.createKeyringMutationGQL.mutate({ createKeyringInput: keyringInput }).pipe(
			map((result) => {
				if (result.errors) {
					throw result.errors;
				}

				if (result.data?.createKeyring?.error) {
					const errorCode = result.data?.createKeyring?.error.code;
					if (errorCode === ErrorCode.InvalidInput || errorCode === ErrorCode.DeviceUnauthenticated) {
						return AuthenticationResultCode.BadPassword;
					}

					return AuthenticationResultCode.Error;
				}

				const keyring = result?.data?.createKeyring?.keyring;
				if (!keyring) {
					return AuthenticationResultCode.Rejected;
				}

				this.sessionKeyringAddDevices(deviceIds, keyring);
				return AuthenticationResultCode.Ok;
			}),

			catchError((error: Error) => {
				this.logger.error('createKeyring()', 'Error while create keyring', { deviceIds, error });
				return of(AuthenticationResultCode.Error);
			})
		);
	}

	/* istanbul ignore next */
	private updateKeyring$(authInfo: AuthenticationInput[]): Observable<AuthenticationResultCode> {
		if (this.sysApiDeviceAuthenticationKeyringService.get() === undefined) {
			return this.createKeyring$(authInfo);
		}
		const deviceIds = authInfo.map((device) => device.deviceId);
		this.logger.trace('updateKeyring()', 'start', deviceIds);
		const keyringInput = authInfo.map((a) => ({ id: a.deviceId, passphrase: a.password }));
		return this.updateKeyringMutationGQL
			.mutate({
				updateKeyringInput: keyringInput,
				currentKeyring: this.sysApiDeviceAuthenticationKeyringService.getForMutation()
			})
			.pipe(
				map((result) => {
					if (result.errors) {
						throw result.errors;
					}

					if (result.data?.updateKeyring?.error) {
						const errorCode = result.data?.updateKeyring?.error.code;
						if (errorCode === ErrorCode.InvalidInput || errorCode === ErrorCode.DeviceUnauthenticated) {
							return AuthenticationResultCode.BadPassword;
						}

						throw new Error('missing keyring in result');
					}

					const keyring = result?.data?.updateKeyring?.keyring;
					if (!keyring) {
						return AuthenticationResultCode.Rejected;
					}

					const authDevices = authInfo.filter((f) => f.password.length > 0).map((d) => d.deviceId);
					if (authDevices.length > 0) {
						this.sessionKeyringAddDevices(authDevices, keyring);
					}
					const unauthDevices = authInfo.filter((f) => f.password.length === 0).map((d) => d.deviceId);
					if (unauthDevices.length > 0) {
						this.sessionKeyringRemoveDevices(unauthDevices, keyring);
					}
					return AuthenticationResultCode.Ok;
				}),

				catchError((error: Error) => {
					this.logger.error('updateKeyring()', 'Error while create keyring', { deviceIds, error });
					return of(AuthenticationResultCode.Error);
				})
			);
	}

	private checkDeviceAuthenticationState(deviceId: string): Promise<boolean> {
		return firstValueFrom(
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId).pipe(map((device) => device.authenticated))
		);
	}

	private async removeDeviceFromKeyring(deviceId: string): Promise<void> {
		this.logger.warning('removeDeviceFromKeyring()', 'device removed from keyring', { deviceId });
		const updateResult = await firstValueFrom(this.updateKeyring$([{ deviceId, password: '' }]));

		if (updateResult === AuthenticationResultCode.Ok) {
			this.deviceAuthenticationStatusService.resetDeviceState(deviceId);
		}

		this.logger.trace('removeDeviceFromKeyring', 'pre condition', {
			deviceId,
			deviceStates: this.deviceAuthenticationStatusService.getDeviceStates()
		});
		this.devicesAuthenticationGqlSubscriptions.unsubscribeQuery(this.deviceQueryIdRecord[deviceId]);
	}

	private validateDeviceResponse(qglResponse: {
		device?: { id: string } | null;
		error?: { code: ErrorCode; message: string } | null;
	}): AuthenticationResponse {
		if (qglResponse?.device?.id === undefined) {
			return { deviceId: '', response: AuthenticationResultCode.Error, error: 'missing id in response!' };
		}
		let itemResponse = AuthenticationResultCode.Ok;
		if (qglResponse.error) {
			itemResponse = AuthenticationResultCode.Error;
			if (
				qglResponse.error?.code === ErrorCode.DeviceUnauthenticated ||
				qglResponse.error?.code === ErrorCode.InvalidInput
			) {
				itemResponse = AuthenticationResultCode.BadPassword;
			}
		}
		return {
			deviceId: qglResponse.device?.id || 'N/A',
			error: qglResponse?.error?.message,
			response: itemResponse
		};
	}

	private mapAuthenticateReponseSet(
		mutationResponse: Array<{ device: { id: string } | null; error: { code: ErrorCode; message: string } | null }>
	): AuthenticationResponseSet {
		const deviceResponses = mutationResponse
			.map((responseDevice) => this.validateDeviceResponse(responseDevice))
			.filter((f) => f.deviceId !== undefined);

		const result: AuthenticationResponseSet = {
			deviceResponses: deviceResponses,
			response: deviceResponses.some((il) => il.response === AuthenticationResultCode.Ok)
				? AuthenticationResultCode.Ok
				: AuthenticationResultCode.Rejected
		};
		return result;
	}
}
